import { createSelector } from '@reduxjs/toolkit';
import { QUOTE_LINE_INFO_TABS } from '@ui/config/price/constants';
import { selectQuoteBuilder } from '@ui/features/quote-builder/state/selectors/quote-builder';
import { RootState } from '@ui/store';

import { IssueAreaType, IssueType } from '../../types/state/issues';
import {
  getIssues as selectIssuesGuard,
  getQuoteBuilderOrThrow,
  getQuoteLineOrThrow,
  type SelectIssueParams,
} from '../utilities/selector-guards';

export const selectIssue = createSelector(
  [(state: RootState) => state.quoteBuilder, (_, issueId: string) => issueId],
  (quoteBuilder, issueId) => {
    const state = getQuoteBuilderOrThrow(quoteBuilder);

    if (!state.issues[issueId]) {
      throw new Error(`Issue ${issueId} not found`);
    }

    const issue = state.issues[issueId];

    if (issue.area === 'snapOperation' || issue.area === 'snapRequirement') {
      const quoteLine = getQuoteLineOrThrow(state, issue.quoteLineUuid);

      return {
        issue,
        isCostEstimatorOpen:
          quoteLine.isInfoVisible &&
          quoteLine.infoTab === QUOTE_LINE_INFO_TABS.COST_ESTIMATOR,
      };
    }

    return { issue };
  },
);

export const selectIssueIds = createSelector(
  [(state: RootState) => state.quoteBuilder],
  (quoteBuilder) => {
    const state = getQuoteBuilderOrThrow(quoteBuilder);
    const issues = state.issues;

    const parentIssues = Object.keys(issues)
      .filter((id) => !issues[id].parentIssueId)
      .sort((a, b) => compareIssues(issues[a], issues[b]));

    const sortedIs: string[] = [];

    for (const issueId of parentIssues) {
      const issue = issues[issueId];

      const childIssueIds = issue.childIssueIds.sort((a, b) =>
        compareIssues(issues[a], issues[b]),
      );

      sortedIs.push(issueId, ...childIssueIds);
    }

    return sortedIs;
  },
);

export const selectAllIssues = (state: RootState) =>
  selectQuoteBuilder(state).issues;

export const selectIssues = createSelector(
  [selectAllIssues, (_, params: SelectIssueParams) => params],
  (issues, params) => {
    return selectIssuesGuard(issues, params);
  },
);

function compareIssues(a: IssueType, b: IssueType): number {
  const areaWeight = {
    quote: 0,
    quoteLine: 1,
    quotePrice: 2,
    snapOperation: 3,
    snapRequirement: 4,
    quoteLineInsights: 5,
  } satisfies Record<IssueAreaType['area'], number>;

  const areaComparison = areaWeight[a.area] - areaWeight[b.area];

  if (areaComparison !== 0) {
    return areaComparison;
  }

  const severityWeight = {
    error: 0,
    warning: 1,
    info: 2,
  };

  return severityWeight[a.severity] - severityWeight[b.severity];
}
